import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../components/Layout/AppHeader';
import { AuthImage } from '../../components/AuthImage/AuthImage';
import { useAuthImage } from '../../hooks/useAuthImage';
import { PhotoViewer } from '../../components/PhotoViewer/PhotoViewer';
import { getImages, getDeliveryNote } from '../../services/api';
import { isOfflineForUser } from '../../services/networkStatus';
import { PdfIcon } from '../../components/DeliveryNoteSection/DeliveryNoteSection';
import { getPendingById, getPendingDetailByDetailId } from '../../services/db';
import { captureError } from '../../services/errorTracking';
import { useAppPreferences, useFeatures } from '../../hooks/useFeatures';
import { getSupplierStamp } from '../../utils/supplierStamp';
import { plateStyle } from '../../services/licensePlate';
import { resolveCountryIso } from '../../services/countries';
import { LicensePlate } from '../../components/LicensePlate/LicensePlate';
import type { DeliveryNote, Detail, ApiImage, Position } from '../../types';

const CONTAINER_TYPE_LABELS: Record<number, string> = {
  1: "40'HC",
  2: "40'DRY",
  3: "40'MAXI",
  4: "20'MAXI",
  5: "20'DRY",
};
import './ViewDetailPage.css';

function LightboxImage({ url, onClose }: { url: string; onClose: () => void }) {
  const src = useAuthImage(url);
  if (!src) return null;
  return <PhotoViewer src={src} onClose={onClose} />;
}

export function ViewDetailPage() {
  const { t, i18n } = useTranslation();
  const { idDetail } = useParams<{ idDetail: string }>();
  const location = useLocation();
  const detail: Detail | undefined = location.state?.detail;
  const position: Position | undefined = location.state?.position;

  const [images, setImages] = useState<ApiImage[]>([]);
  // Fotos añadidas offline que aún viven en la cola (pending NEW o pending
  // EDIT). Se pintan junto a las del servidor para que el usuario las vea y
  // pueda confiar en que no se han perdido aunque la sync no haya corrido.
  const [pendingImages, setPendingImages] = useState<ApiImage[]>([]);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!idDetail) return;
    const id = Number(idDetail);
    // idDetail < 0 ⇒ es un NEW pendiente (DetailPage le asigna -pendingId).
    // No tiene contraparte en servidor, así que no llamamos a getImages.
    if (id <= 0) {
      setImages([]);
      return;
    }
    getImages(id)
      .then((r) => {
        // La API devuelve { listDataImage: [...] }
        const data = r.data;
        const list: ApiImage[] = Array.isArray(data)
          ? data
          : (data as { listDataImage?: ApiImage[] })?.listDataImage ?? [];
        setImages(list);
      })
      .catch((e) => {
        captureError(e, { where: 'ViewDetailPage.getImages', idDetail: id });
        setImages([]);
      });
  }, [idDetail]);

  useEffect(() => {
    if (!idDetail) return;
    const id = Number(idDetail);
    let cancelled = false;
    (async () => {
      try {
        const pending = id < 0
          ? await getPendingById(-id)
          : await getPendingDetailByDetailId(id);
        if (cancelled) return;
        const list = (pending?.detail?.listNewImages ?? []) as ApiImage[];
        setPendingImages(list);
      } catch (e) {
        captureError(e, { where: 'ViewDetailPage.loadPendingImages', idDetail });
        if (!cancelled) setPendingImages([]);
      }
    })();
    return () => { cancelled = true; };
  }, [idDetail]);

  const prefs = useAppPreferences();
  const features = useFeatures();

  // Documento de transporte (DDT) del contenedor (2026-10-23), para quien lo
  // gestiona: aquí solo se ve; se sube y se quita desde la tarjeta o la ficha.
  const [deliveryNote, setDeliveryNote] = useState<DeliveryNote | null>(null);
  useEffect(() => {
    const id = Number(idDetail);
    if (!features.deliveryNote || !(id > 0) || isOfflineForUser()) return;
    let cancelled = false;
    getDeliveryNote(id)
      .then((r) => { if (!cancelled) setDeliveryNote(r.data ? r.data : null); })
      .catch(() => { /* sin respuesta: no se enseña */ });
    return () => { cancelled = true; };
  }, [features.deliveryNote, idDetail]);
  const supplierStamp = getSupplierStamp(detail, i18n.language);

  // Parsea "Ubicación ( email@ctrl.com )" → { location, controller }
  function parseLocation(raw: string | undefined): { loc: string; controller: string } {
    if (!raw) return { loc: '', controller: '' };
    const match = raw.match(/^(.*?)\s*\(\s*(.*?)\s*\)\s*$/);
    if (match) return { loc: match[1].trim(), controller: match[2].trim() };
    return { loc: raw.trim(), controller: '' };
  }
  const { loc: parsedLoc, controller } = parseLocation(detail?.location);

  // El backend guarda en VgmConfirmation la respuesta completa de PortIC
  // (p.ej. "OK REF, ABC123"). Para mostrar solo la referencia nos quedamos
  // con lo que va detrás de la primera coma; si no hay coma, se enseña entero.
  function parseVgmReference(raw: string | undefined): string {
    if (!raw) return '';
    const idx = raw.indexOf(',');
    return idx >= 0 ? raw.slice(idx + 1).trim() : raw.trim();
  }
  const vgmRef = parseVgmReference(detail?.vgmConfirmation);

  if (!detail) {
    return (
      <div className="view-page">
        <AppHeader title={t('view.title')} showBack />
        <p className="view-empty">{t('view.noData')}</p>
      </div>
    );
  }

  // Matrículas con el estilo de la placa de su país, como en la lista.
  const loadCountry = resolveCountryIso(position?.countryCode, position?.countryName);
  const plateNode = (value: string | undefined) =>
    value?.trim() ? <LicensePlate number={value} style={plateStyle(value, loadCountry)} /> : undefined;

  const rows: { label: string; value: string | number | undefined; node?: ReactNode }[] = [
    { label: t('view.container'), value: detail.containerNumber },
    { label: t('view.containerType'), value: detail.idContainerType ? CONTAINER_TYPE_LABELS[detail.idContainerType] : undefined },
    // Matrículas y VGM no aplican al perfil self-managed: se omiten las filas
    // en lugar de mostrarlas vacías.
    ...(features.truckPlate ? [
      { label: t('view.vehiclePlate'), value: detail.numberPlat, node: plateNode(detail.numberPlat) },
      { label: t('view.trailerPlate'), value: detail.trailerNumberPlat, node: plateNode(detail.trailerNumberPlat) },
    ] : []),
    { label: t('view.seal'), value: detail.sealNumber },
    { label: t('view.vetSeal'), value: detail.sealVeter },
    { label: t('view.carrier'), value: detail.carrierName },
    { label: t('view.bundles'), value: detail.packageNumber },
    { label: t('view.humidity'), value: detail.humidity != null ? `${detail.humidity}%` : undefined },
    { label: t('view.tare'), value: detail.tare != null ? `${detail.tare} Tn` : undefined },
    { label: t('view.netWeight'), value: detail.netWeight != null ? `${detail.netWeight} Tn` : undefined },
    ...(features.vgm ? [
      { label: t('view.vgm'), value: detail.vgm != null ? `${detail.vgm} Tn` : undefined },
    ] : []),
    { label: t('view.checkIn'), value: detail.checkIn ? detail.checkIn.substring(0, 16) : undefined },
    { label: t('view.lastUpdate'), value: detail.checkOut ? detail.checkOut.substring(0, 16) : undefined },
    ...(features.supplierStamp && supplierStamp ? [
      { label: t('view.supplierFilled'), value: `${supplierStamp.who}${supplierStamp.when ? ' · ' + supplierStamp.when : ''}` },
    ] : []),
    { label: t('view.exitTime'), value: detail.comments },
    ...(prefs.locationEnabled ? [
      { label: t('view.lotNumber'), value: detail.lotNumber },
      { label: t('view.location'), value: parsedLoc || undefined },
      ...(controller ? [{ label: t('view.controller'), value: controller }] : []),
    ] : []),
  ];

  return (
    <div className="view-page">
      <AppHeader title={detail.containerNumber || t('view.title')} showBack />

      <div className="view-content">
        {vgmRef && (
          <div className="vgm-flag" title={detail.vgmConfirmation}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span className="vgm-flag__label">{t('view.vgmOk')}</span>
            <span className="vgm-flag__ref">{vgmRef}</span>
          </div>
        )}
        <div className="view-card">
          {rows.filter((r) => r.value != null && r.value !== '').map((r) => (
            <div className="view-row" key={r.label}>
              <span className="view-row__label">{r.label}</span>
              <span className="view-row__value">{r.node ?? r.value}</span>
            </div>
          ))}
        </div>

        {deliveryNote && (
          <div className="view-card view-ddt">
            <PdfIcon />
            <div className="view-ddt__info">
              <span className="view-row__label">{t('deliveryNote.title')}</span>
              <span className="view-ddt__name">{deliveryNote.fileName}</span>
            </div>
            <a className="view-ddt__btn" href={deliveryNote.url} target="_blank" rel="noreferrer noopener">
              {t('deliveryNote.viewPdf')}
            </a>
          </div>
        )}

        {(images.length > 0 || pendingImages.length > 0) && (
          <div className="view-photos">
            <h3 className="view-photos__title">
              {t('view.photos', { count: images.length + pendingImages.length })}
            </h3>
            <div className="view-photo-grid">
              {images.map((img, i) => (
                <div key={`s-${i}`} className="view-photo-thumb">
                  <AuthImage
                    src={img.urlImage}
                    alt={img.imageName}
                    className="view-photo-img"
                    onClick={() => setLightbox(img.urlImage)}
                  />
                </div>
              ))}
              {pendingImages.map((img, i) => (
                <div
                  key={`p-${i}`}
                  className="view-photo-thumb view-photo-thumb--pending"
                  onClick={() => setLightbox(img.urlImage)}
                >
                  <img
                    src={img.urlImage}
                    alt={img.imageName}
                    className="view-photo-img"
                  />
                  <span className="view-photo-pending-badge">{t('view.pending')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox && <LightboxImage url={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
